import { FC, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useFormikContext } from 'formik';
import { InputCheck, InputText, InputTextArea } from '@farmshare/ui-components';
import { Cut } from '../../types';
import { ImageUploadInput } from '../builder/image-upload-input';

const TabPanel: FC<{
  value: number;
  index: number;
  children: React.ReactNode;
}> = ({ value, index, children }) => (
  <Box hidden={value !== index} sx={{ pt: 2 }}>
    {value === index && children}
  </Box>
);

interface ChoiceOption {
  id: string;
  label: string;
  description?: string;
  imageUrl?: string;
  cutIds: string[];
}

interface FormValues {
  title: string;
  description?: string;
  required?: boolean;
  allowSubSelection?: boolean;
  options: ChoiceOption[];
}

interface ChoiceGroupQuestionFormProps {
  cuts?: Cut[];
  /** The capability set ID used to group uploaded images in S3. */
  capabilityId: string;
}

/**
 * Form for creating/editing Choice Group questions
 * Used with useModal pattern
 * @param props - Component props
 * @param props.cuts - Available cuts from capabilities
 * @param props.capabilityId - The capability set ID for S3 image grouping
 * @returns Choice group question form component
 */
export const ChoiceGroupQuestionForm: FC<ChoiceGroupQuestionFormProps> = ({
  cuts = [],
  capabilityId,
}) => {
  const [tab, setTab] = useState(0);
  const { values, setFieldValue } = useFormikContext<FormValues>();

  const addOption = () => {
    const newOption: ChoiceOption = {
      id: crypto.randomUUID(),
      label: '',
      description: '',
      cutIds: [],
    };

    setFieldValue('options', [...(values.options || []), newOption]);
  };

  const updateOption = (optionId: string, updates: Partial<ChoiceOption>) => {
    setFieldValue(
      'options',
      (values.options || []).map((option) =>
        option.id === optionId ? { ...option, ...updates } : option,
      ),
    );
  };

  const removeOption = (optionId: string) => {
    setFieldValue(
      'options',
      (values.options || []).filter((option) => option.id !== optionId),
    );
  };

  return (
    <Box>
      <Tabs
        value={tab}
        onChange={(_, newTab) => setTab(newTab)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
      >
        <Tab label="Details" />
        <Tab label="Options" />
      </Tabs>

      <TabPanel value={tab} index={0}>
        <Stack spacing={3}>
          <InputText label="Question Title" nameOverride="title" required />
          <InputTextArea
            label="Description"
            nameOverride="description"
            rows={2}
            hint="Optional description to help customers understand their options"
          />
          <Stack direction="row" spacing={2}>
            <Box flex={1}>
              <InputCheck
                label="Required"
                nameOverride="required"
                hint="Whether customers must select one of the options"
              />
            </Box>
            <Box flex={1}>
              <InputCheck
                label="Let customer pick cuts within the chosen option"
                nameOverride="allowSubSelection"
                hint={
                  'When on, after picking an option the customer chooses which specific cuts inside it they want. When off, all cuts in the chosen option are included automatically.'
                }
              />
            </Box>
          </Stack>
        </Stack>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography variant="subtitle2">Choice Options</Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={addOption}
            variant="contained"
            size="small"
          >
            Add Option
          </Button>
        </Stack>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 2, display: 'block' }}
        >
          Each option can contain multiple cuts. Customers will select one
          option from the group.
        </Typography>

        {values.options && values.options.length > 0 ? (
          <Stack spacing={2}>
            {values.options.map((option, index) => (
              <Paper key={option.id} variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={2}
                  >
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>
                      Option {index + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => removeOption(option.id)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>

                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <ImageUploadInput
                      nameOverride={`options.${index}.imageUrl`}
                      label="Image"
                      capabilityId={capabilityId}
                      avatar
                    />
                    <Stack spacing={2} flex={1}>
                      <TextField
                        label="Option Label"
                        value={option.label}
                        onChange={(e) =>
                          updateOption(option.id, { label: e.target.value })
                        }
                        required
                        fullWidth
                        size="small"
                      />

                      <TextField
                        label="Option Description"
                        value={option.description || ''}
                        onChange={(e) =>
                          updateOption(option.id, {
                            description: e.target.value,
                          })
                        }
                        multiline
                        rows={2}
                        fullWidth
                        size="small"
                      />
                    </Stack>
                  </Stack>

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 1 }}
                    >
                      Cuts included when the customer picks this option
                    </Typography>
                    {cuts.length === 0 ? (
                      <Paper
                        sx={{
                          p: 2,
                          textAlign: 'center',
                          bgcolor: 'grey.50',
                          border: '1px dashed',
                          borderColor: 'divider',
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          No cuts available in the catalog yet.
                        </Typography>
                      </Paper>
                    ) : (
                      <Autocomplete
                        multiple
                        size="small"
                        options={cuts}
                        getOptionLabel={(cut) => cut.title}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        value={cuts.filter((cut) =>
                          option.cutIds.includes(cut.id),
                        )}
                        onChange={(_, newValue) =>
                          updateOption(option.id, {
                            cutIds: newValue.map((cut) => cut.id),
                          })
                        }
                        renderOption={(props, cut) => (
                          <Box component="li" {...props} key={cut.id}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={500}>
                                {cut.title}
                              </Typography>
                              {cut.description && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'block', lineHeight: 1.3 }}
                                >
                                  {cut.description}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select cuts..."
                            size="small"
                          />
                        )}
                      />
                    )}
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Stack>
        ) : (
          <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
            <Typography variant="body2" color="text.secondary">
              No options added yet. Click &quot;Add Option&quot; to create
              choice options.
            </Typography>
          </Paper>
        )}
      </TabPanel>
    </Box>
  );
};
